// The pricing calculator's state, as URL search params.
//
// The URL IS the save: a scenario worth keeping is a bookmark, and nothing is
// written anywhere. So every input lives here, and only the ones somebody set:
// a param left out means the default, which keeps a fresh calculator a clean
// `/pricing` and a link short enough to read.
//
// Amounts are MAJOR units of the scenario's currency (`rate=0.75`, `saas=4000`),
// because this is a URL a person reads; they become minor units once, in
// `searchToInputs`. Every field carries `.catch(undefined)`, as the other
// search schemas do, so a mangled param falls back to its default instead of
// breaking the page.

import { z } from "zod";
import type { Currency } from "@crm/shared";
import {
  USAGE_PRICINGS,
  type Averaging,
  type AllowanceUnit,
  type ImplementationFee,
  type UsagePricing,
} from "./pricingModel.js";

const IsoMonthPattern = /^\d{4}-\d{2}$/;

/** A number, whether the router hands it over parsed or as text. */
const num = (min: number, max: number) =>
  z.coerce.number().min(min).max(max).optional().catch(undefined);

const PricingSearchSchema = z.object({
  /** The month the comparables are measured over. Absent means the month just gone. */
  month: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  /** The comparable customers' deal ids, comma separated. */
  from: z.string().max(4_000).optional().catch(undefined),
  avg: z.enum(["weighted", "mean"]).optional().catch(undefined),
  cur: z.enum(["USD", "CAD"]).optional().catch(undefined),
  /** Cost basis, typed over what the comparables measured. */
  cpm: num(0, 1_000),
  mpc: num(0, 600),
  rup: num(0, 1),
  usage: z
    .enum(USAGE_PRICINGS as [UsagePricing, ...UsagePricing[]])
    .optional()
    .catch(undefined),
  rate: num(0, 1_000_000),
  /** The monthly hosting fee. */
  host: num(0, 100_000_000),
  /** The old single platform fee, read as hosting so a link made before the split still opens. */
  saas: num(0, 100_000_000),
  /** The prepaid block: how many minutes or calls, and what the month's block costs. */
  inc: num(0, 1_000_000_000),
  incu: z.enum(["minutes", "calls"]).optional().catch(undefined),
  pre: num(0, 100_000_000),
  /** Implementation fees as `amount@month`, comma separated: `40000@1,40000@7`. */
  impl: z.string().max(1_000).optional().catch(undefined),
  /** The partner and agreement the shares were taken from, for the label. */
  partner: z.string().max(100).optional().catch(undefined),
  ag: z.string().max(100).optional().catch(undefined),
  /** Our shares, in percent. */
  sr: num(0, 100),
  si: num(0, 100),
  sc: num(0, 100),
  calls: num(0, 100_000_000),
  ramp: num(0, 60),
  rstart: num(0, 100),
  term: num(1, 120),
  /** The first contract month, for the timeline's axis. */
  start: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  human: num(0, 100_000),
  target: num(0, 99.9),
});

export type PricingSearch = z.infer<typeof PricingSearchSchema>;

export function validatePricingSearch(raw: Record<string, unknown>): PricingSearch {
  const parsed = PricingSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/**
 * Where a fresh calculator starts: LA County's $1.55 a conversation, a
 * thousand calls a month on a year's contract, sold direct, against $6 a call
 * for a person (the ROI calculator's figure). Amounts are USD; a CAD scenario
 * converts them.
 */
export const DEFAULTS = {
  usage: "per_call" as UsagePricing,
  rateUsd: 1.55,
  calls: 1_000,
  term: 12,
  humanUsd: 6,
  shares: 100,
  includedUnit: "minutes" as AllowanceUnit,
  averaging: "weighted" as Averaging,
  currency: "USD" as Currency,
} as const;

/** Every input, resolved: defaults filled in, amounts in minor units of `currency`. */
export interface PricingInputs {
  comparablesMonth: string | null;
  comparableDealIds: readonly string[];
  averaging: Averaging;
  currency: Currency;
  /** Typed over the measured basis. Null keeps what was measured. */
  costPerMinuteMinor: number | null;
  minutesPerCall: number | null;
  roundUpMinutesPerCall: number | null;
  usage: UsagePricing;
  rateMinor: number;
  hostingMinor: number;
  prepaidQuantity: number;
  prepaidUnit: AllowanceUnit;
  prepaidAmountMinor: number;
  implementation: ImplementationFee[];
  partnerId: string | null;
  agreementId: string | null;
  recurringSharePct: number;
  implementationSharePct: number;
  costSharePct: number;
  callsPerMonth: number;
  rampMonths: number;
  rampStartPct: number;
  termMonths: number;
  startMonth: string | null;
  humanCostPerCallMinor: number;
  targetMarginPct: number | null;
}

/**
 * The inputs this URL asks for. `usdToCad` converts the USD defaults for a CAD
 * scenario; amounts the URL states are already in its currency.
 */
export function searchToInputs(raw: PricingSearch, usdToCad: number): PricingInputs {
  const s = validatePricingSearch(raw as Record<string, unknown>);
  const currency = s.cur ?? DEFAULTS.currency;
  const fromUsd = currency === "CAD" ? usdToCad : 1;
  const minor = (major: number | undefined, defaultUsd: number): number =>
    (major ?? defaultUsd * fromUsd) * 100;
  return {
    comparablesMonth: s.month ?? null,
    comparableDealIds: parseIds(s.from),
    averaging: s.avg ?? DEFAULTS.averaging,
    currency,
    costPerMinuteMinor: s.cpm === undefined ? null : s.cpm * 100,
    minutesPerCall: s.mpc ?? null,
    roundUpMinutesPerCall: s.rup ?? null,
    usage: s.usage ?? DEFAULTS.usage,
    rateMinor: minor(s.rate, DEFAULTS.rateUsd),
    hostingMinor: minor(s.host ?? s.saas, 0),
    prepaidQuantity: s.inc ?? 0,
    prepaidUnit: s.incu ?? DEFAULTS.includedUnit,
    prepaidAmountMinor: minor(s.pre, 0),
    implementation: parseImplementation(s.impl),
    partnerId: s.partner ?? null,
    agreementId: s.ag ?? null,
    recurringSharePct: s.sr ?? DEFAULTS.shares,
    implementationSharePct: s.si ?? DEFAULTS.shares,
    costSharePct: s.sc ?? DEFAULTS.shares,
    callsPerMonth: s.calls ?? DEFAULTS.calls,
    rampMonths: Math.round(s.ramp ?? 0),
    rampStartPct: s.rstart ?? 25,
    termMonths: Math.round(s.term ?? DEFAULTS.term),
    startMonth: s.start ?? null,
    humanCostPerCallMinor: minor(s.human, DEFAULTS.humanUsd),
    targetMarginPct: s.target ?? null,
  };
}

/** The keys that hold an amount of money, and so move when the currency does. */
const MONEY_KEYS = ["cpm", "rate", "host", "saas", "pre", "human"] as const;

/**
 * The same scenario in another currency: every amount the URL states is
 * converted, and the defaults it leaves out convert on their own. A unit price
 * keeps four places, an amount two, so a round trip stays a round trip.
 */
export function convertSearch(
  search: PricingSearch,
  to: Currency,
  usdToCad: number,
): PricingSearch {
  const from = search.cur ?? DEFAULTS.currency;
  if (from === to) return search;
  const factor = to === "CAD" ? usdToCad : 1 / usdToCad;
  const next: PricingSearch = { ...search, cur: to === DEFAULTS.currency ? undefined : to };
  for (const key of MONEY_KEYS) {
    const value = search[key];
    if (value === undefined) continue;
    const places = key === "cpm" || key === "rate" ? 4 : 2;
    next[key] = roundTo(value * factor, places);
  }
  const fees = parseImplementation(search.impl);
  if (fees.length > 0) {
    next.impl = formatImplementation(
      fees.map((fee) => ({ ...fee, amountMinor: Math.round(fee.amountMinor * factor) })),
    );
  }
  return next;
}

/**
 * A search with one change applied. Undefined drops the param, so a value set
 * back to its default does not linger in the link.
 */
export function withParam<K extends keyof PricingSearch>(
  search: PricingSearch,
  key: K,
  value: PricingSearch[K],
): PricingSearch {
  const next = { ...search };
  if (value === undefined || value === "") delete next[key];
  else next[key] = value;
  return next;
}

export function parseIds(raw: string | undefined): string[] {
  if (raw === undefined) return [];
  return [
    ...new Set(
      raw
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== ""),
    ),
  ];
}

export function formatIds(ids: readonly string[]): string | undefined {
  return ids.length === 0 ? undefined : ids.join(",");
}

/** `40000@1,40000@7` → two fees, in minor units. An unreadable entry is left out. */
export function parseImplementation(raw: string | undefined): ImplementationFee[] {
  if (raw === undefined) return [];
  const fees: ImplementationFee[] = [];
  for (const part of raw.split(",")) {
    const match = /^\s*(\d+(?:\.\d{1,2})?)@(\d{1,3})\s*$/.exec(part);
    if (match === null) continue;
    const month = Number(match[2]);
    if (month < 1) continue;
    fees.push({ amountMinor: Math.round(Number(match[1]) * 100), month });
  }
  return fees;
}

export function formatImplementation(fees: readonly ImplementationFee[]): string | undefined {
  if (fees.length === 0) return undefined;
  return fees.map((fee) => `${trimZeros(fee.amountMinor / 100, 2)}@${fee.month}`).join(",");
}

function roundTo(value: number, places: number): number {
  const scale = 10 ** places;
  return Math.round(value * scale) / scale;
}

function trimZeros(value: number, places: number): string {
  const fixed = value.toFixed(places);
  return fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
}
